import { useAuth0 } from "@auth0/auth0-react";
import { Grid, Input, Button, Spin, Message } from "@arco-design/web-react";
import React, { useEffect, useMemo } from 'react';
import styled from "styled-components";
import { useNavigate } from "react-router-dom";
import { FetchApi, SaveApi } from "../../Api/AuthenticatedApi";
import { formEqual } from "../../utils/formEqual";
import { useUnsavedGuard } from "../../utils/useUnsavedGuard";
import Content from "../../Layout/Content";

const { Row, Col } = Grid;

// Always-editable settings form: fields are live, Save enables only when
// something changed vs. the loaded record, and Discard reverts while dirty.

const FormControl = styled.div`
  margin: 8px;
  display: inline-flex;
  flex-direction: column;
  width: 100%;
`;

const FieldLabel = styled.div`
  font-size: 12px;
  color: var(--color-text-3);
  margin-bottom: 4px;
`;

// Single source of truth for "the form's values as loaded". setFromRecord and the
// dirty baseline both derive from this so they can never disagree.
function valuesFromRecord(r) {
    return {
        address: (r && r.address) || "",
        address2: (r && r.address2) || "",
        city: (r && r.city) || "",
        state: (r && r.state) || "",
        zip: (r && r.zip) || "",
    };
}

export default function OrganizationAddress() {
    const [address, setAddress] = React.useState("");
    const [address2, setAddress2] = React.useState("");
    const [city, setCity] = React.useState("");
    const [state, setState] = React.useState("");
    const [zip, setZip] = React.useState("");

    const { getAccessTokenSilently } = useAuth0();
    const [record, setRecord] = React.useState({});
    const [loading, setLoading] = React.useState(true);



    let history = useNavigate();

    useEffect(() => {
        (async () => {
            let url = "/api/settings/organization"
            FetchApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to load organization address");
                })
        })();
    }, [setRecord, setLoading, history, getAccessTokenSilently])

    function saveSettings(data) {
        (async () => {
            let url = "/api/settings/organization/address"
            SaveApi(getAccessTokenSilently, url,
                (data) => {
                    setLoading(false);
                    setRecord(data);
                    Message.success("Organization address saved");
                },
                (response) => {
                    setLoading(false);
                    history("/login");
                }, (response) => {
                    setLoading(false);
                    Message.error("Failed to save organization address");
                }, data);
        })();
    }

    const save = () => {
        saveSettings({ address, address2, city, state, zip });
    }

    const setFromRecord = (r) => {
        const v = valuesFromRecord(r);
        setAddress(v.address);
        setAddress2(v.address2);
        setCity(v.city);
        setState(v.state);
        setZip(v.zip);
    }

    const discard = () => setFromRecord(record);

    React.useEffect(() => {
        setFromRecord(record)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record])

    const baseline = useMemo(() => valuesFromRecord(record), [record]);
    const current = { address, address2, city, state, zip };
    const isDirty = !formEqual(current, baseline);
    useUnsavedGuard(isDirty);


    if (loading) {
        return (
            <Content>
                <Spin />
            </Content>
        );
    }

    return (
        <Row>
            <Col span={24}>
                <FormControl>
                    <FieldLabel>Address</FieldLabel>
                    <Input id="address" value={address} onChange={(v) => setAddress(v)} />
                </FormControl>
            </Col>



            <Col span={24}>
                <FormControl>
                    <FieldLabel>Address2</FieldLabel>
                    <Input id="address2" value={address2} onChange={(v) => setAddress2(v)} />
                </FormControl>
            </Col>

            <Col span={24}>
                <FormControl>
                    <FieldLabel>City</FieldLabel>
                    <Input id="city" value={city} onChange={(v) => setCity(v)} />
                </FormControl>
            </Col>

            <Col span={12}>
                <FormControl>
                    <FieldLabel>State</FieldLabel>
                    <Input id="state" value={state} onChange={(v) => setState(v)} />
                </FormControl>
            </Col>

            <Col span={12}>
                <FormControl>
                    <FieldLabel>Zip</FieldLabel>
                    <Input id="zip" value={zip} onChange={(v) => setZip(v)} />
                </FormControl>
            </Col>


            <Col span={24}>
                &nbsp;
            </Col>

            <Col span={24} style={{ display: "flex", alignItems: "center", gap: 8, paddingLeft: 8 }}>
                <Button type="primary" onClick={save} disabled={!isDirty}>Save</Button>
                {isDirty && <Button onClick={discard}>Discard</Button>}
            </Col>
        </Row>

    );
}
